import * as React from 'react';
import { Link } from 'react-router';

import {
  Button,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Skeleton,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react';
import {
  Persona,
  PersonaAvatar,
  PersonaDetails,
  PersonaLabel,
  PersonaSecondaryLabel,
} from '@saas-ui/react';
import { LogOut, User } from 'lucide-react';

import { ICONS_OPACITY } from '@/constant';
import { useLogout, useUser } from '@/store/auth';

interface UserMenuProps {
  isMobileMenuOpen: boolean;
  onNavClick: () => void;
  onOpenChange?: (isOpen: boolean) => void;
}

export const UserMenu: React.FC<UserMenuProps> = ({
  isMobileMenuOpen,
  onNavClick,
  onOpenChange,
}) => {
  const { user, isLoading } = useUser();
  const logout = useLogout();
  const isMobile = useBreakpointValue({ base: true, lg: false });

  const getDisplayName = () => {
    if (!user) return 'Loading...';
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    return user.email || 'No email provided';
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  if (isLoading) return <Skeleton height={10} rounded='md' />;

  if (!user) return null;

  return (
    <Menu
      isLazy
      strategy='fixed'
      placement={isMobileMenuOpen ? 'top' : 'right-end'}
      flip={isMobileMenuOpen}
      gutter={8}
      onOpen={() => onOpenChange?.(true)}
      onClose={() => onOpenChange?.(false)}
    >
      <MenuButton
        as={Button}
        size='md'
        w='full'
        paddingInline={0}
        background='transparent'
        data-cy='user-menu-button'
        _hover={{
          background: 'transparent',
        }}
        _active={{
          background: 'transparent',
        }}
        _focus={{
          background: 'transparent',
        }}
        _focusVisible={{
          background: 'transparent',
        }}
      >
        <Persona gap={3}>
          <PersonaAvatar
            name={getDisplayName()}
            src={user.profile_picture?.url}
            size='sm'
            presence='online'
          />
          {(isMobile || isMobileMenuOpen) && (
            <PersonaDetails textAlign='left' marginInlineStart={0}>
              <PersonaLabel
                fontSize='sm'
                lineHeight='5'
                fontWeight='normal'
                isTruncated
              >
                {getDisplayName()}
              </PersonaLabel>
              <PersonaSecondaryLabel
                fontSize='xs'
                lineHeight='5'
                fontWeight='normal'
                isTruncated
              >
                {user?.email}
              </PersonaSecondaryLabel>
            </PersonaDetails>
          )}
        </Persona>
      </MenuButton>
      <Portal>
        <MenuList
          py={1}
          px={1}
          bg='cards.bg'
          borderRadius='lg'
          border='1px solid!'
          borderColor='border.light!'
          boxShadow='none!'
          minW='12rem'
          zIndex='popover'
        >
          <MenuItem
            as={Link}
            to='/profile'
            onClick={onNavClick}
            data-cy='user-menu-view-profile'
            gap={2}
            fontSize='sm'
            lineHeight='5'
            fontWeight='medium'
            rounded='md'
            bg='transparent'
            className='group'
            whiteSpace='nowrap'
            _hover={{ bg: 'whiteAlpha.50', color: 'accent.500' }}
          >
            <Icon
              as={User}
              boxSize={4}
              opacity={ICONS_OPACITY}
              _groupHover={{ opacity: 1 }}
            />
            <Text fontSize='sm'>View Profile</Text>
          </MenuItem>
          <MenuItem
            onClick={handleLogout}
            data-cy='user-menu-sign-out'
            gap={2}
            fontSize='sm'
            lineHeight='5'
            fontWeight='medium'
            rounded='md'
            bg='transparent'
            color='accent.500'
            whiteSpace='nowrap'
            _hover={{ bg: 'whiteAlpha.50' }}
          >
            <Icon as={LogOut} boxSize={4} />
            <Text fontSize='sm'>Sign Out</Text>
          </MenuItem>
        </MenuList>
      </Portal>
    </Menu>
  );
};
